/**
 * `reportError(error, context)` tells this app's own server that something broke in a browser. The server writes one
 * log line (see /api/client-errors in src/server/client-errors.ts) and keeps nothing. A telemetry service, if we add
 * one later, plugs in on the server side, so the browser never talks to anyone else.
 *
 * What a report carries: the screen's path (no query string), the message, the top of the stack, the app version
 * and a hash of the person's id. Never store data, never a token: secrets are scrubbed here and again on the server.
 * At most a few reports a minute leave one tab, and the same error twice in that minute is sent once.
 */
import { scrubSecrets } from '../shared/redact'
import type { ClientErrorKind, ClientErrorReport } from '../shared/types'
import { APP_VERSION } from './version'

export interface ReportContext {
  kind: ClientErrorKind
  /** The reference shown to the person, if any. */
  ref?: string
  componentStack?: string
}

const MAX_MESSAGE = 500
const MAX_STACK = 4000
const STACK_LINES = 15

export function trimStack(stack: string | undefined): string | undefined {
  if (!stack) return undefined
  return scrubSecrets(stack.split('\n').slice(0, STACK_LINES).join('\n')).slice(0, MAX_STACK)
}

/** One-way hash of an id (hex, 16 chars). Undefined where the browser has no WebCrypto (plain http). */
export async function hashId(id: string): Promise<string | undefined> {
  if (!globalThis.crypto?.subtle) return undefined
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(id))
  return Array.from(new Uint8Array(digest).slice(0, 8), (b) => b.toString(16).padStart(2, '0')).join('')
}

export interface ReporterDeps {
  send: (report: ClientErrorReport) => Promise<void>
  now: () => number
  /** The current path, without the query string. */
  route: () => string
  version: string
  user: () => string | undefined
  /** Reports per window. */
  limit?: number
  windowMs?: number
}

/** Builds a reporter. Returns the send promise, or undefined when the report was dropped by the rate limit. */
export function createReporter(deps: ReporterDeps) {
  const limit = deps.limit ?? 5
  const windowMs = deps.windowMs ?? 60_000
  let sentAt: number[] = []
  const lastSeen = new Map<string, number>()

  return function report(error: unknown, context: ReportContext): Promise<void> | undefined {
    const now = deps.now()
    sentAt = sentAt.filter((t) => now - t < windowMs)
    const err = error instanceof Error ? error : new Error(String(error))
    const message = scrubSecrets(err.message || err.name || 'Unknown error').slice(0, MAX_MESSAGE)
    const route = deps.route().split(/[?#]/)[0] || '/'
    const signature = `${context.kind}|${route}|${message}`
    const seen = lastSeen.get(signature)
    if (seen !== undefined && now - seen < windowMs) return undefined
    if (sentAt.length >= limit) return undefined
    sentAt.push(now)
    lastSeen.set(signature, now)
    if (lastSeen.size > 50) lastSeen.delete(lastSeen.keys().next().value!)

    const user = deps.user()
    const stack = trimStack(err.stack)
    const componentStack = trimStack(context.componentStack)
    const payload: ClientErrorReport = {
      kind: context.kind,
      route,
      message,
      ...(stack ? { stack } : {}),
      ...(componentStack ? { componentStack } : {}),
      ...(context.ref ? { ref: context.ref } : {}),
      version: deps.version,
      ...(user ? { user } : {}),
      at: new Date(now).toISOString(),
    }
    return deps.send(payload).catch(() => {})
  }
}

// ---------------------------------------------------------------------------------------------------------------
// The app's one reporter. The Gate calls configureReporting once the person is signed in; before that, reports
// are dropped (every /api route needs a sign-in token, this one included).

const current: { getToken?: () => Promise<string | undefined>; user?: string } = {}

export function configureReporting(opts: { getToken: () => Promise<string | undefined>; userId?: string }) {
  current.getToken = opts.getToken
  if (opts.userId) {
    void hashId(opts.userId).then(
      (h) => (current.user = h),
      () => {},
    )
  }
}

async function postReport(report: ClientErrorReport): Promise<void> {
  if (!current.getToken) return
  const token = await current.getToken().catch(() => undefined)
  if (!token) return
  // keepalive lets the report finish even if the tab reloads straight after (the stale-chunk case).
  await fetch('/api/client-errors', {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(report),
  })
}

export const reportError = createReporter({
  send: postReport,
  now: () => Date.now(),
  route: () => (typeof location === 'undefined' ? '/' : location.pathname),
  version: APP_VERSION,
  user: () => current.user,
})
